import { addApolloState, initializeApollo } from '@/utils/apolloClient'
import type { NextPage, GetServerSidePropsContext } from 'next'
import React from 'react'
import { useQuery } from '@apollo/client'
import {
  Box,
  Card,
  Center,
  Link,
  Heading,
  Text,
  Container,
  Flex,
  Image,
  useColorModeValue,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import ReactMarkdown from 'react-markdown'

import { customerReviews } from 'data/customerReviews'
import { getHomePageData } from '@/api/strapi/homePage'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { CustomerReviews } from '@/components/CustomerReviews'
import { Hero } from '@/components/Hero'
import { Loading } from '@/components/Loading'
import { Metrics } from '@/components/Metrics'
import { SectionWithCards } from '@/components/SectionWithCards'
import { SubscribeCard } from '@/components/SubscribeCard'
import { VerticalSlideshow } from '@/components/VerticalSlideshow'
import Carousel from '@/components/carousel/Carousel'
import { RightArrowIcon } from '@/components/icons/RightArrowIcon'
import { getFeaturedMiners } from '@/api/minersListing'
import { ProductCard } from '@/features/home/ProductCard'
import { MostRecentCompassContent } from '@/features/home/MostRecentCompassContent'
import { TalktoSales } from '@/features/home/TalktoSales'
import { IMiner } from '@/types/miner.interface'
import { IHomePage, IPress } from '@/types/strapi/homePage.interface'
import { getStrapiMedia } from '@/utils/getStrapiMedia'

type Props = {
  homePageData: IHomePage
}

const HomePage: NextPage<Props> = ({ homePageData }) => {
  const t = useTranslations()
  const secondaryBg = useColorModeValue(
    'lightModeSecondaryBG',
    'darkModeSecondaryBG'
  )

  const { data, loading } = useQuery(getFeaturedMiners, {
    fetchPolicy: 'cache-first',
  })

  return (
    <>
      <Hero />
      {homePageData && (
        <Box bg={secondaryBg} id="why-choose-compass">
          <ContainerBase
            maxWidth={{ xxl: 'container.xl' }}
            py={{ base: 8, lg: 20 }}
          >
            <Metrics
              title={homePageData.statisticsTitle}
              subtitle={homePageData.statisticsSubtitle}
              statistics={homePageData.statistics}
            />
          </ContainerBase>
        </Box>
      )}
      {homePageData?.whyUs?.published && (
        <Box id="why-us">
          <ContainerBase
            maxWidth={{ xxl: 'container.xl' }}
            py={{ base: 8, lg: 20 }}
          >
            <VerticalSlideshow
              title={homePageData.whyUs.title}
              slideshow={homePageData.whyUs.slideshow}
            />
          </ContainerBase>
        </Box>
      )}
      {homePageData?.howItWorks?.published && (
        <Box bg={secondaryBg} id="how-it-works">
          <ContainerBase
            maxWidth={{ xxl: 'container.xl' }}
            py={{ base: 8, lg: 20 }}
          >
            <SectionWithCards
              title={homePageData.howItWorks.title}
              subtitle={homePageData.howItWorks.subtitle}
              link={homePageData.howItWorks.link}
              cards={homePageData.howItWorks.cards}
              columns={3}
            />
          </ContainerBase>
        </Box>
      )}
      {loading ? (
        <Loading text={t('hardware-list.loading')} />
      ) : (
        data?.allAvailableProducts?.edges?.length > 0 && (
          <ContainerBase
            maxWidth={{ xxl: 'container.xl' }}
            pt={{ base: 8, lg: 20 }}
            pb={{ base: 2, lg: 6 }}
          >
            <Heading
              variant="spaceGrotesk"
              as="h2"
              fontSize="32px"
              textAlign="center"
              fontWeight={300}
              mb={{ base: 9, lg: 12 }}
            >
              {t('home.featured-hardware')}
            </Heading>
            <Container
              maxW={{
                base: '100%',
                lg: '885px',
                xl: '1200px',
              }}
              p={0}
            >
              <Carousel
                gap={30}
                containerHeight={{ base: 'auto', xl: '620px' }}
                containerWidth={1}
                showBottomSlider={data?.allAvailableProducts?.edges?.length > 1}
                alwaysFullWidthContainer
              >
                {data?.allAvailableProducts?.edges.map(({ node }: IMiner) => (
                  <ProductCard node={node} key={node.productId} />
                ))}
              </Carousel>
            </Container>
            <Center mt="7">
              <Link href="/hardware" variant="black" textDecoration="underline">
                {t('home.shop-all')}
              </Link>
            </Center>
          </ContainerBase>
        )
      )}
      {homePageData?.advertisementTop?.published && (
        <ContainerBase py={8} id="advertisement-top" maxWidth={'xl'}>
          <Card
            direction={{ base: 'column-reverse', md: 'row' }}
            alignItems="center"
            overflow="hidden"
            bg="black.300"
            color="white"
          >
            {homePageData.advertisementTop.image && (
              <Box
                width={{ base: '100%', md: '40%' }}
                flexGrow={1}
                flexShrink={1}
                flexBasis="100%"
                alignSelf="stretch"
              >
                <Image
                  objectFit="cover"
                  height="100%"
                  src={getStrapiMedia(homePageData.advertisementTop.image.url)}
                  alt={
                    homePageData.advertisementTop.image.alternativeText ||
                    homePageData.advertisementTop.title
                  }
                />
              </Box>
            )}
            <Box
              width={{ base: '100%', md: '60%' }}
              px={{ base: '16px', lg: '48px' }}
              py={{ base: '16px', lg: '48px' }}
              flexGrow={1}
              flexShrink={1}
              flexBasis="100%"
            >
              {homePageData.advertisementTop.subtitle && (
                <Flex direction={'row'} alignItems="center" mb={2}>
                  {homePageData.advertisementTop.subtitleIcon && (
                    <Image
                      mr={2}
                      maxWidth="32px"
                      src={getStrapiMedia(
                        homePageData.advertisementTop.subtitleIcon.url
                      )}
                      alt={
                        homePageData.advertisementTop.subtitleIcon
                          .alternativeText || ''
                      }
                    />
                  )}
                  <Text
                    fontSize={{ base: '20px', md: '24px' }}
                    letterSpacing="-0.8px"
                    fontWeight={500}
                  >
                    {homePageData.advertisementTop.subtitle}
                  </Text>
                </Flex>
              )}
              <Text
                mb={2}
                fontSize={{ base: '28px', md: '32px' }}
                letterSpacing="-0.8px"
                lineHeight="106.25%"
                fontWeight={700}
                variant="brandGradient"
              >
                {homePageData.advertisementTop.title}
              </Text>
              {homePageData.advertisementTop.text && (
                <Text mb={{ base: 4, md: 8 }} lineHeight="1.25">
                  {homePageData.advertisementTop.text}
                </Text>
              )}
              {homePageData.advertisementTop.link && (
                <Link
                  href={homePageData.advertisementTop.link.url}
                  isExternal={homePageData.advertisementTop.link.newTab}
                >
                  {homePageData.advertisementTop.link.text}
                  <RightArrowIcon pl={1} width={{ base: '12px' }} />
                </Link>
              )}
            </Box>
          </Card>
        </ContainerBase>
      )}
      <Center id="reviews" py={'60px'}>
        <CustomerReviews
          title={t('home.world-class-service')}
          reviews={customerReviews}
        />
      </Center>
      <ContainerBase maxWidth={'container.xxl'} pt={'36px'} pb={'60px'}>
        <Center flexDirection={'column'}>
          <Heading
            variant="spaceGrotesk"
            as="h2"
            fontSize="36px"
            textAlign="center"
            fontWeight={300}
            mb={{ base: 9, lg: 12 }}
          >
            {homePageData?.pressTitle || t('home.press')}
          </Heading>
          <Flex
            flexWrap="wrap"
            maxWidth={{ base: '100%', xl: '1278px' }}
            gridGap={{ base: '20px', md: '33px' }}
            justifyContent="center"
          >
            {homePageData?.press?.map((newsItem: IPress, i: number) => (
              <Link
                key={`press-${i}`}
                href={newsItem?.url}
                target="_blank"
                isExternal
              >
                <Box
                  width={{ base: '100%', md: '294px' }}
                  height={{ base: 'auto', md: '135px' }}
                >
                  <Image
                    border="1px"
                    borderColor="blackAlpha.500"
                    src={getStrapiMedia(newsItem.image.url)}
                    alt={
                      newsItem.image?.alternativeText ||
                      `${t('home.press')} ${i + 1}`
                    }
                    objectFit={'cover'}
                    width="100%"
                    height="100%"
                  />
                </Box>
              </Link>
            ))}
          </Flex>
        </Center>
      </ContainerBase>
      <TalktoSales talkToSales={homePageData?.talkToSales || []} />
      <MostRecentCompassContent />
      <Box bg="blackAlpha.900" position="relative" overflow="hidden">
        <Box
          zIndex={1}
          position="absolute"
          top="-35%"
          left="50%"
          w="1064px"
          h="1064px"
          rounded="100%"
          background="radial-gradient(65.37% 65.37% at 52.49% 48.96%, #0060FF 0%, #00FFBD 100%);"
          filter="blur(200px)"
          transform={{ base: 'translateX(-10%)', md: 'translateX(-50%)' }}
        ></Box>
        <ContainerBase
          maxW={'container.lg'}
          py={8}
          position="relative"
          zIndex={2}
        >
          <SubscribeCard
            title={t('home.newsletter.title')}
            subtitle={t('home.newsletter.subtitle')}
          />
        </ContainerBase>
      </Box>
      {homePageData?.disclaimer && (
        <ContainerBase py={4}>
          <Center fontSize="sm" fontWeight={400} opacity={0.6}>
            <ReactMarkdown>{homePageData.disclaimer}</ReactMarkdown>
          </Center>
        </ContainerBase>
      )}
    </>
  )
}

export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  const apolloClient = initializeApollo()
  const { data } = await apolloClient.query({
    query: getHomePageData,
    variables: {
      locale,
    },
    context: {
      clientName: 'strapi',
    },
  })

  if (!data) {
    return addApolloState(apolloClient, {
      notFound: true,
    })
  }

  const homePageData = data?.homePage || {}

  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
      isHomePage: true,
      homePageData,
    },
  }
}

export default HomePage
